import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";


export const CalendarConnectionPanel = ({ connection, events, eventDraft, busy, onDraftChange, onSelectProvider, onConnectGoogle, onSyncGoogle, onDisconnect, onImportEvent }) => {
  const selectedGoogle = connection?.selected_provider === "google_calendar";
  const selectedApple = connection?.selected_provider === "apple_calendar";
  const providerRows = connection?.providers || [];
  const googleRow = providerRows.find((provider) => provider.provider === "google_calendar");
  const appleRow = providerRows.find((provider) => provider.provider === "apple_calendar");

  return (
    <section className="space-y-4 rounded-[28px] border border-stone-200 bg-white/90 p-4" data-testid="profile-calendar-card">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-stone-500" data-testid="profile-calendar-kicker">
          Calendar
        </p>
        <h2 className="mt-2 text-2xl font-semibold text-stone-900" data-testid="profile-calendar-title">
          Agenda context sync
        </h2>
        <p className="mt-2 text-sm text-stone-600" data-testid="profile-calendar-description">
          Google Calendar kan nu veilig verbinden en syncen via server-side OAuth. Apple Calendar blijft hier een seam/placeholder. Je tokens blijven alleen backend-side opgeslagen.
        </p>
      </div>

      <div className="rounded-[22px] border border-dashed border-stone-300 bg-stone-50 p-3" data-testid="profile-calendar-redirect-card">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" data-testid="profile-calendar-redirect-title">Redirect URI</p>
        <p className="mt-2 break-all text-xs text-stone-600" data-testid="profile-calendar-redirect-uri">{connection?.redirect_uri}</p>
      </div>

      <div className="space-y-3" data-testid="profile-calendar-provider-rows">
        <div className="rounded-[22px] border border-stone-200 bg-stone-50 p-3" data-testid="profile-calendar-provider-google-row">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-stone-900" data-testid="profile-calendar-provider-google-label">Google Calendar</p>
              <p className="mt-1 text-xs text-stone-500" data-testid="profile-calendar-provider-google-status">{googleRow?.status_label || "Ready to connect"}</p>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="outline" className="rounded-full border-stone-200 bg-white px-3 text-xs" onClick={() => onSelectProvider("google_calendar")} disabled={busy} data-testid="profile-calendar-provider-google-select-button">
                {selectedGoogle ? "Actief" : "Actief maken"}
              </Button>
              {googleRow?.can_sync ? (
                <Button variant="outline" className="rounded-full border-stone-200 bg-white px-3 text-xs" onClick={onSyncGoogle} disabled={busy} data-testid="profile-calendar-provider-google-sync-button">
                  Sync nu
                </Button>
              ) : (
                <Button variant="outline" className="rounded-full border-stone-200 bg-white px-3 text-xs" onClick={onConnectGoogle} disabled={busy} data-testid="profile-calendar-provider-google-connect-button">
                  Connect Google
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-[22px] border border-stone-200 bg-stone-50 p-3" data-testid="profile-calendar-provider-apple-row">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-stone-900" data-testid="profile-calendar-provider-apple-label">Apple Calendar</p>
              <p className="mt-1 text-xs text-stone-500" data-testid="profile-calendar-provider-apple-status">{appleRow?.status_label || "Placeholder seam"}</p>
            </div>
            <Button variant="outline" className="rounded-full border-stone-200 bg-white px-3 text-xs" onClick={() => onSelectProvider("apple_calendar")} disabled={busy} data-testid="profile-calendar-provider-apple-select-button">
              {selectedApple ? "Actief" : "Actief maken"}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs" data-testid="profile-calendar-status-row">
        <span className="rounded-full bg-stone-100 px-3 py-1 text-stone-700" data-testid="profile-calendar-selected-provider-pill">
          {connection?.selected_provider ? `Provider: ${connection.selected_provider === "google_calendar" ? "Google Calendar" : "Apple Calendar"}` : "Nog geen provider actief"}
        </span>
        <span className="rounded-full bg-stone-100 px-3 py-1 text-stone-700" data-testid="profile-calendar-last-sync-pill">
          {connection?.last_sync_at ? `Laatste sync: ${new Date(connection.last_sync_at).toLocaleString("nl-NL")}` : "Nog niet gesynchroniseerd"}
        </span>
        <Button variant="outline" className="h-8 rounded-full border-stone-200 bg-white px-3 text-xs" onClick={onDisconnect} disabled={busy} data-testid="profile-calendar-disconnect-button">
          Loskoppelen
        </Button>
      </div>

      <div className="rounded-[22px] border border-dashed border-stone-300 bg-stone-50 p-3" data-testid="profile-calendar-import-card">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" data-testid="profile-calendar-import-title">Event import</p>
        <div className="mt-3 space-y-3">
          <Input value={eventDraft.title} onChange={(event) => onDraftChange("title", event.target.value)} placeholder="Bijv. Team dinner" className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="profile-calendar-event-title-input" />
          <div className="grid grid-cols-2 gap-3">
            <Input type="datetime-local" value={eventDraft.start_at} onChange={(event) => onDraftChange("start_at", event.target.value)} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="profile-calendar-event-start-input" />
            <Input type="datetime-local" value={eventDraft.end_at} onChange={(event) => onDraftChange("end_at", event.target.value)} className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="profile-calendar-event-end-input" />
          </div>
          <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
            <Select value={eventDraft.provider} onValueChange={(value) => onDraftChange("provider", value)}>
              <SelectTrigger className="h-12 rounded-2xl border-stone-200 bg-white" data-testid="profile-calendar-event-provider-select">
                <SelectValue placeholder="Kies provider" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="google_calendar">Google Calendar</SelectItem>
                <SelectItem value="apple_calendar">Apple Calendar</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 rounded-full bg-white px-3 py-2 text-xs font-medium text-stone-700" data-testid="profile-calendar-event-all-day-wrap">
              <input type="checkbox" checked={eventDraft.all_day} onChange={(event) => onDraftChange("all_day", event.target.checked)} data-testid="profile-calendar-event-all-day-checkbox" />
              All-day
            </label>
          </div>
          <Button className="h-12 w-full rounded-full bg-stone-900 text-white hover:bg-stone-800" onClick={onImportEvent} disabled={busy} data-testid="profile-calendar-import-button">
            Importeer event
          </Button>
        </div>
      </div>

      <div className="space-y-2" data-testid="profile-calendar-events-list">
        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500" data-testid="profile-calendar-events-title">Recente events</p>
        {events.length ? events.slice(0, 4).map((event) => (
          <div key={event.id} className="rounded-[20px] border border-stone-200 bg-stone-50 px-3 py-2" data-testid={`profile-calendar-event-row-${event.id}`}>
            <p className="text-sm font-medium text-stone-900" data-testid={`profile-calendar-event-title-${event.id}`}>{event.title}</p>
            <p className="mt-1 text-xs text-stone-500" data-testid={`profile-calendar-event-meta-${event.id}`}>{event.provider} · {event.all_day ? "All-day" : "Tijdgebonden"} · {new Date(event.start_at).toLocaleString("nl-NL")}</p>
          </div>
        )) : <p className="text-sm text-stone-500" data-testid="profile-calendar-events-empty">Nog geen events geïmporteerd.</p>}
      </div>
    </section>
  );
};